import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { communityTopics } from "@/mocks/community";
import useDocumentTitle from "@/hooks/useDocumentTitle";
import Breadcrumb from "@/components/base/Breadcrumb";
import EmptyState from "@/components/base/EmptyState";

interface Reply {
  id: string;
  author: string;
  time: string;
  content: string;
}

interface Comment {
  id: string;
  author: string;
  time: string;
  content: string;
  replies: Reply[];
}

export default function TopicDetail() {
  const { topicId } = useParams();
  const topic = communityTopics.find((t) => t.id === topicId);
  useDocumentTitle(
    topic ? `${topic.title} - 漫蛙2动漫社区` : "话题不存在 - 漫蛙2动漫社区",
    topic ? topic.content.slice(0, 120) : "该讨论话题不存在或已删除。"
  );

  const [comments, setComments] = useState<Comment[]>(() =>
    (topic?.comments ?? []).map((c) => ({ ...c, replies: [...c.replies] }))
  );
  const [draft, setDraft] = useState("");
  const [toast, setToast] = useState("");
  const [expanded, setExpanded] = useState<string[]>([]);

  const relatedTopics = useMemo(() => {
    if (!topic) return [];
    const same = communityTopics.filter((t) => t.id !== topic.id && t.category === topic.category);
    const rest = communityTopics.filter((t) => t.id !== topic.id && t.category !== topic.category);
    return [...same, ...rest].slice(0, 4);
  }, [topic]);

  if (!topic) {
    return (
      <div className="page-wrap py-16">
        <EmptyState
          icon="ri-chat-delete-line"
          title="话题不存在或已删除"
          description="该讨论可能已被移除，返回社区看看其他热门话题吧。"
          actionLabel="返回社区"
          actionTo="/community"
        />
      </div>
    );
  }

  const showToast = (message: string) => {
    setToast(message);
    setTimeout(() => setToast(""), 2600);
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    const value = draft.trim();
    if (!value) return;
    setComments((prev) => [
      { id: `local-${Date.now()}`, author: "我（体验用户）", time: "刚刚", content: value, replies: [] },
      ...prev,
    ]);
    setDraft("");
    showToast("演示效果：评论仅显示在本地，不会真实提交");
  };

  const toggleReplies = (id: string) => {
    setExpanded((prev) => (prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]));
  };

  return (
    <div className="pb-4">
      <div className="page-wrap py-6 md:py-8">
        <Breadcrumb items={[{ label: "动漫社区", to: "/community" }, { label: topic.title }]} />
      </div>

      <section className="page-wrap grid grid-cols-1 gap-8 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <article className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
            <span className="rounded-full bg-secondary-100 px-3 py-1 text-xs font-medium text-secondary-800">
              {topic.category}
            </span>
            <h1 className="mt-4 text-xl font-extrabold leading-snug text-foreground-950 md:text-2xl">{topic.title}</h1>
            <div className="mt-4 flex flex-wrap items-center gap-4 border-b border-background-200 pb-4">
              <div className="flex items-center gap-2.5">
                <div className="h-9 w-9 overflow-hidden rounded-full bg-background-200">
                  <img
                    src={topic.avatar}
                    alt={`${topic.author} 头像`}
                    title={`${topic.author} - 漫蛙2社区`}
                    className="h-full w-full object-cover object-top"
                  />
                </div>
                <div>
                  <p className="text-sm font-semibold text-foreground-900">{topic.author}</p>
                  <p className="text-xs text-foreground-400">{topic.createdAt}</p>
                </div>
              </div>
              <div className="ml-auto flex items-center gap-4 text-xs text-foreground-500">
                <span className="flex items-center gap-1">
                  <i className="ri-chat-3-line"></i>
                  {topic.replies} 回复
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-eye-line"></i>
                  {topic.views}
                </span>
              </div>
            </div>
            <p className="mt-5 text-[15px] leading-[1.9] text-foreground-800">{topic.content}</p>
          </article>

          <section className="mt-8">
            <h2 className="text-lg font-bold text-foreground-950">评论区（{comments.length}）</h2>
            <p className="mt-1 text-sm text-foreground-500">以下为演示交互，评论不会真实保存或提交。</p>

            <form onSubmit={handleSubmit} className="mt-4 rounded-lg border border-background-200 bg-background-50 p-4">
              <textarea
                value={draft}
                onChange={(e) => setDraft(e.target.value.slice(0, 500))}
                rows={3}
                placeholder="写下你的看法，友善交流（演示）"
                className="w-full resize-none rounded-md border border-background-200 bg-background-100 p-3 text-sm text-foreground-900 outline-none placeholder:text-foreground-400 focus:border-accent-400"
              />
              <div className="mt-3 flex items-center justify-between">
                <span className="text-xs text-foreground-400">{draft.length} / 500</span>
                <button
                  type="submit"
                  className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
                >
                  <i className="ri-send-plane-line"></i>
                  发表评论
                </button>
              </div>
            </form>

            {toast && (
              <div className="mt-3 flex items-center gap-2 rounded-md bg-accent-50 px-4 py-3 text-sm text-accent-800">
                <i className="ri-information-line"></i>
                {toast}
              </div>
            )}

            <ul className="mt-5 flex flex-col gap-4">
              {comments.map((comment) => (
                <li key={comment.id} className="rounded-lg border border-background-200 bg-background-50 p-4">
                  <div className="flex items-center gap-2">
                    <span className="flex h-8 w-8 items-center justify-center rounded-full bg-background-100 text-xs font-semibold text-foreground-600">
                      {comment.author.slice(0, 1)}
                    </span>
                    <span className="text-sm font-semibold text-foreground-900">{comment.author}</span>
                    <span className="text-xs text-foreground-400">{comment.time}</span>
                  </div>
                  <p className="mt-3 text-sm leading-relaxed text-foreground-800">{comment.content}</p>
                  {comment.replies.length > 0 && (
                    <>
                      <button
                        type="button"
                        onClick={() => toggleReplies(comment.id)}
                        className="mt-3 inline-flex items-center gap-1 text-xs font-medium text-accent-700 hover:text-accent-800"
                      >
                        <i className={expanded.includes(comment.id) ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"}></i>
                        {expanded.includes(comment.id) ? "收起" : `展开 ${comment.replies.length} 条回复`}
                      </button>
                      {expanded.includes(comment.id) && (
                        <ul className="mt-3 space-y-3 border-l-2 border-background-200 pl-4">
                          {comment.replies.map((reply) => (
                            <li key={reply.id}>
                              <div className="flex items-center gap-2">
                                <span className="text-xs font-semibold text-foreground-800">{reply.author}</span>
                                <span className="text-xs text-foreground-400">{reply.time}</span>
                              </div>
                              <p className="mt-1 text-sm text-foreground-700">{reply.content}</p>
                            </li>
                          ))}
                        </ul>
                      )}
                    </>
                  )}
                </li>
              ))}
            </ul>
          </section>
        </div>

        <aside className="lg:col-span-1">
          <h2 className="text-lg font-bold text-foreground-950">相关话题推荐</h2>
          <ul className="mt-4 flex flex-col gap-3">
            {relatedTopics.map((item) => (
              <li key={item.id}>
                <Link
                  to={`/community/${item.id}`}
                  className="group block rounded-lg border border-background-200 bg-background-50 p-4 transition-colors hover:border-primary-300"
                >
                  <h3 className="line-clamp-2 text-sm font-medium text-foreground-900 transition-colors group-hover:text-primary-700">
                    {item.title}
                  </h3>
                  <div className="mt-2 flex items-center gap-3 text-xs text-foreground-400">
                    <span className="rounded-full bg-secondary-100 px-2 py-0.5 text-secondary-800">{item.category}</span>
                    <span className="flex items-center gap-1">
                      <i className="ri-chat-3-line"></i>
                      {item.replies}
                    </span>
                  </div>
                </Link>
              </li>
            ))}
          </ul>
          <Link
            to="/community"
            className="mt-4 inline-flex items-center gap-1 whitespace-nowrap rounded-md border border-background-200 px-4 py-2 text-sm font-medium text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600"
          >
            <i className="ri-arrow-left-line"></i>
            返回社区首页
          </Link>
        </aside>
      </section>
    </div>
  );
}